import React, {useState, useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';
import {Col, Row, Input, Label, Alert, Modal, ModalHeader, ModalBody} from 'reactstrap';
import {useDispatch, useSelector} from 'react-redux';

import {authUserManager} from "../../helpers/api_helper";
import {uploadEventLogo} from "../../slices/event/details/thunk";
import {logoUploadReset} from "../../slices/event/details/reducer";

const ClubLogo = ({eventName, description, facebookLink, tiktokLink, youtubeLink, websiteLink,  instagramLink, userName}) => {
  const {t} = useTranslation();
  const {eventId} = useParams();
  const dispatch = useDispatch();

  const isClubAdmin = authUserManager.isClubAdmin();

  const {logoUploadSuccess, logoUploadError, logoUploading, violations} = useSelector((state) => ({
    logoUploadSuccess: state.Event.logoUploadSuccess,
    logoUploadError: state.Event.logoUploadError,
    logoUploading: state.Event.logoUploading,
    violations: state.Event.violations,
  }));

  const logoSrc = `${process.env.REACT_APP_API_URL.replace(/\/$/, '')}/guest/events/${eventId}/logo`;

  const [src, setSrc] = useState(logoSrc);
  const [validationError, setValidationError] = useState(null);
  const [descriptionModalOpen, setDescriptionModalOpen] = useState(false);

  const toggleDescriptionModal = () => setDescriptionModalOpen((open) => !open);

  const openDescriptionModal = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDescriptionModalOpen(true);
  };

  const MAX_FILE_SIZE = 524288; // 0.5MB in bytes
  const ALLOWED_FILE_TYPES = ['image/jpeg', 'image/jpg', 'image/png'];

  const validateFile = (file) => {
    if (!file) {
      return t('No file selected');
    }

    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      return t('Invalid file type. Only JPEG and PNG files are allowed');
    }

    if (file.size > MAX_FILE_SIZE) {
      return t('File size exceeds 0.5MB limit');
    }

    return null;
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setValidationError(null);
    dispatch(logoUploadReset());

    if (!file) {
      return;
    }

    const error = validateFile(file);
    if (error) {
      setValidationError(error);
      e.target.value = '';
      return;
    }

    dispatch(uploadEventLogo(eventId, file));
  };

  useEffect(() => {
    if (logoUploadSuccess) {
      setSrc(`${logoSrc}?t=${Date.now()}`);
      setTimeout(() => {
        dispatch(logoUploadReset());
      }, 3000);
    }
  }, [logoUploadSuccess, logoSrc, dispatch]);

  return (
    <div className="w-100 d-flex justify-content-center">
      <Row className="justify-content-center p-2">
        <Col xs="auto">
          {isClubAdmin && validationError && (
            <Alert color="danger" className="mb-2">
              {validationError}
            </Alert>
          )}
          {isClubAdmin && logoUploadError && violations && (
            <Alert color="danger" className="mb-2">
              {typeof violations === 'string' ? violations : t('Failed to upload logo')}
            </Alert>
          )}
          {isClubAdmin && logoUploadSuccess && (
            <Alert color="success" className="mb-2">
              {t('Logo uploaded successfully!')}
            </Alert>
          )}
          <Row className="align-items-center">
            <Col xs="auto" className="pe-0">
              <div className="avatar-md profile-user position-relative d-inline-block mx-auto">
                <img
                  src={src}
                  alt="user-img"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: 'center'
                  }}
                  className="img-thumbnail rounded-circle"
                />
                {isClubAdmin && (
                  <div className="avatar-xs p-0 rounded-circle profile-photo-edit">
                    <Input
                      id="profile-img-file-input"
                      type="file"
                      className="profile-img-file-input"
                      accept="image/jpeg,image/jpg,image/png"
                      onChange={handleFileChange}
                      disabled={logoUploading}
                    />
                    <Label htmlFor="profile-img-file-input"
                           className="profile-photo-edit avatar-xs mb-0">
                                                      <span className="avatar-title rounded-circle bg-dark text-body fs-16">
                                                          {logoUploading ? <i className="ri-loader-4-line"></i> : <i className="ri-camera-fill"></i>}
                                                      </span>
                    </Label>
                  </div>
                )}
              </div>
            </Col>

            <Col>
              {userName ? (
                <>
                  <h5 className="mb-0">{userName}</h5>
                  <p className="text-white text-opacity-75 mb-1">{eventName}</p>
                </>
              ) : (
                <h4 className="mb-1">{eventName}</h4>
              )}
              {description && (
                <a
                  href="#"
                  onClick={openDescriptionModal}
                  className="text-white text-opacity-75 text-decoration-underline d-inline-block mb-1"
                >
                  {t('About event...')}
                </a>
              )}
              <div className="hstack gap-2 mt-0">
                {facebookLink && <a
                  href={facebookLink}
                  target="_blank"
                  rel="noreferrer"
                  className="text-decoration-none d-inline-flex align-items-center justify-content-center rounded-circle social-icon"
                  aria-label="Facebook"
                >
                  <i className="ri-facebook-circle-line fs-2"/>
                </a>}
                {instagramLink && <a
                  href={instagramLink}
                  target="_blank"
                  rel="noreferrer"
                  className="text-decoration-none d-inline-flex align-items-center justify-content-center rounded-circle social-icon"
                  aria-label="Instagram"
                >
                  <i className="ri-instagram-line fs-2"/>
                </a>}
                {tiktokLink && <a
                  href={tiktokLink}
                  target="_blank"
                  rel="noreferrer"
                  className="text-decoration-none d-inline-flex align-items-center justify-content-center rounded-circle social-icon"
                  aria-label="TikTok"
                >
                  <i className="ri-tiktok-fill fs-2"/>
                </a>}
                {youtubeLink && <a
                  href={youtubeLink}
                  target="_blank"
                  rel="noreferrer"
                  className="text-decoration-none d-inline-flex align-items-center justify-content-center rounded-circle social-icon"
                  aria-label="Youtube"
                >
                  <i className="ri-youtube-line fs-2"/>
                </a>}
                {websiteLink && <a
                  href={websiteLink}
                  target="_blank"
                  rel="noreferrer"
                  className="text-decoration-none d-inline-flex align-items-center justify-content-center rounded-circle social-icon"
                  aria-label="Website"
                >
                  <i className="ri-global-line fs-2"/>
                </a>}
              </div>
            </Col>
          </Row>
        </Col>
      </Row>
      {description && (
        <Modal isOpen={descriptionModalOpen} toggle={toggleDescriptionModal} centered>
          <ModalHeader toggle={toggleDescriptionModal}>{eventName}</ModalHeader>
          <ModalBody style={{whiteSpace: 'pre-wrap'}}>
            {description}
          </ModalBody>
        </Modal>
      )}
    </div>
  )
};

export default ClubLogo;
